import { Divider, Menu, Typography } from '@material-ui/core';
import React, { FC } from 'react';

import { Action } from 'src/StatusLabel';
import { StatusLabelClassNames } from 'src/types';

import { StatusLabelMenuActions } from './StatusLabelMenuActions';

/**
 * The label and the extra lines are read-only text. Inside a menu they are
 * presentational, because a menu owns only menu items. Inside a popover that has
 * no actions the list is a plain list, and these are its items — the only place
 * that text is announced at all.
 */
const InfoRow: FC<{ presentational: boolean }> = ({ children, presentational }) => (
  <Typography component="li" role={presentational ? 'presentation' : undefined} variant="overline">
    {children}
  </Typography>
);

export interface StatusLabelMenuProps {
  options: {
    fullLabel?: string;
    additionalInfo?: string[];
  };
  actions: Action[];
  anchorEl: null | HTMLElement;
  handleClose: () => void;
}

export const StatusLabelMenu: FC<StatusLabelMenuProps> = ({
  handleClose,
  options: { additionalInfo = [], fullLabel = '' },
  anchorEl,
  actions,
}) => {
  const hasFullLabel = !!fullLabel;
  const hasAdditionalInfo = !!additionalInfo.length;
  const hasActions = !!actions.length;

  // `MenuList` puts `role="menu"` on its own `<ul>`, and a menu owns menu items.
  // A popover carrying only label rows has none, so calling it a menu leaves an
  // empty one that announces nothing. It is a list instead.
  //
  // Naming the role on `Menu` as well put a second one on the Popover root, so
  // the list announced as a menu inside a menu whose only child was a `div`.
  const listRole = hasActions ? 'menu' : 'list';

  // `MenuList` gives its first child the opening focus, and here that is the
  // label row rather than an action. `disableAutoFocusItem` leaves the focus on
  // the list, which is where the arrow keys are read, and the first action takes
  // it once the menu has opened. With no actions the list keeps the focus, so a
  // screen reader reads its rows instead of resting on the paper.
  const focusFirstAction = (element: HTMLElement) => {
    const firstAction = element.querySelector<HTMLElement>('[role="menuitem"]');
    (firstAction ?? element.querySelector<HTMLElement>(`.${StatusLabelClassNames.MENU_CONTAINER}`))?.focus();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      classes={{ paper: StatusLabelClassNames.MENU_PAPER }}
      disableAutoFocusItem
      elevation={0}
      getContentAnchorEl={null}
      keepMounted
      MenuListProps={{ className: StatusLabelClassNames.MENU_CONTAINER, role: listRole }}
      onClose={handleClose}
      onEntered={focusFirstAction}
      open={Boolean(anchorEl)}
      variant="menu"
    >
      {hasFullLabel && <InfoRow presentational={hasActions}>{fullLabel}</InfoRow>}
      {hasFullLabel && (hasAdditionalInfo || hasActions) && <Divider />}
      {additionalInfo.map(info => (
        <InfoRow key={info} presentational={hasActions}>
          {info}
        </InfoRow>
      ))}
      {hasAdditionalInfo && hasActions && <Divider />}
      {hasActions && <StatusLabelMenuActions actions={actions} handleClose={handleClose} />}
    </Menu>
  );
};
